import { LoginResponse, MsLoginRequest, MsLoginResponse, PaginationHeader } from '@/models/auth';
import { Booking, Company, Machine, PostBooking, Project, UserInfo } from '@/models/bookings';
import { Driver, User } from '@/models/users';
import store from '@/store';
import { GETTERS } from '@/store/getters';
import { DEFAULT_LIMIT_PARAM } from '@/utils/constants';
import axios from 'axios';

export const login = async (username: string, password: string): Promise<LoginResponse> => {
  const authorizationBasic = Buffer.from(username + ':' + password).toString('base64');

  const { data } = await axios.get<LoginResponse>(process.env.VUE_APP_API_BASE_URL + 'account', {
    headers: {
      Authorization: 'Basic ' + authorizationBasic,
    },
  });
  return data;
};

export const msLogin = async (tenant_id: string, userProfile: MsLoginRequest): Promise<MsLoginResponse> => {
  const { data } = await axios.post<MsLoginResponse>(
    process.env.VUE_APP_API_BASE_URL + `ssocustomers/${tenant_id}/users`,
    userProfile,
  );
  return data;
};

export const getUserInfo = async (user_id: number): Promise<UserInfo> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  const { data } = await axios.get<UserInfo>(process.env.VUE_APP_API_BASE_URL + 'admin/users/' + user_id, {
    headers: {
      apiKey,
    },
  });
  return data;
};

export const getCompanyById = async (company_id: number): Promise<Company> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  const { data } = await axios.get<Company>(process.env.VUE_APP_API_BASE_URL + `admin/company/${company_id}`, {
    params: { includeSettings: true },
    headers: {
      apiKey,
    },
  });
  return data;
};

export const getProjects = async (company_id: number): Promise<Project[]> => {
  return sendPaginatedRequest<Project>('admin/projects/company/' + company_id + '?offset={offset}');
};

export const createBooking = async (booking: PostBooking): Promise<number> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  const { data } = await axios.post<{ id: number }>(
    process.env.VUE_APP_API_BASE_URL + 'vehiclebooking/bookings',
    booking,
    {
      headers: {
        apiKey,
      },
    },
  );
  return data.id;
};

export const updateBooking = async (booking_id: number, booking: PostBooking): Promise<void> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  await axios.put(process.env.VUE_APP_API_BASE_URL + 'vehiclebooking/bookings/' + booking_id, booking, {
    headers: {
      apiKey,
    },
  });
};

export const stopBooking = async (booking_id: number, booking: PostBooking): Promise<void> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  await axios.put(process.env.VUE_APP_API_BASE_URL + 'vehiclebooking/bookings/' + booking_id + '/stop', booking, {
    headers: {
      apiKey,
    },
  });
};

export const getBookings = async (
  start_date: string,
  end_date: string,
  machine_ids?: number[],
  limit?: number,
): Promise<Booking[]> => {
  let url = `vehiclebooking/bookings?offset={offset}&startDate=${start_date}&endDate=${end_date}`;
  if (machine_ids) {
    url = url.concat(machine_ids ? `&machineIds=${machine_ids.join(',')}` : '');
  }

  return sendPaginatedRequest<Booking>(url, limit);
  // Remove duplicates.
  // TODO double check that.
  // const bookings_map = new Map<number, Booking>();
  // bookings.forEach((booking) => bookings_map.set(booking.id, booking));
  // return Array.from(bookings_map.values());
};

export const getBookingById = async (booking_id: number): Promise<Booking> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  const { data } = await axios.get<Booking>(
    process.env.VUE_APP_API_BASE_URL + `vehiclebooking/bookings/${booking_id}`,
    {
      headers: {
        apiKey,
      },
    },
  );
  return data;
};

export const deleteBooking = async (booking_id: number): Promise<void> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];
  await axios.delete(process.env.VUE_APP_API_BASE_URL + `vehiclebooking/bookings/${booking_id}`, {
    headers: {
      apiKey,
    },
  });
};

export const getMachines = async (): Promise<Machine[]> => {
  return sendPaginatedRequest<Machine>('vehiclebooking/machines?offset={offset}');
};

// user GET /users endpoint and format into Driver type
// NOTE: the logged in user is not returned in the response!
export const getDrivers = async (): Promise<Driver[]> => {
  const users = await sendPaginatedRequest<User>('admin/users?offset={offset}');

  return users.map((user) => {
    return {
      company: {
        id: user.companyId,
      },
      driver: {
        email: user.email,
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
      },
    };
  });
};

const sendPaginatedRequest = async <T>(base_url: string, limit = DEFAULT_LIMIT_PARAM): Promise<T[]> => {
  const apiKey = store.getters[GETTERS.GET_API_KEY];

  const result: T[] = [];

  let offset = 0;
  let next_iteration = true;
  while (next_iteration) {
    const replaced_url =
      process.env.VUE_APP_API_BASE_URL + base_url.replace('{offset}', `${offset}`) + `&limit=${limit}`;
    const response = await axios.get<T[]>(replaced_url, {
      headers: {
        apiKey,
      },
    });

    result.push(...response.data);

    const pagination: PaginationHeader = JSON.parse(response.headers['x-pagination']);

    offset += pagination.Limit;

    if (response.data.length < (pagination.Limit ?? limit)) {
      next_iteration = false;
    }
  }

  return result;
};
